import { useEffect, useMemo } from 'react';
import { i18next, useTranslation } from '@frontend/i18n';
import { Button, PopoverMenu, RadioField, useForm, usePopoverMenu, Chip, Text, styles } from '@frontend/design-system';
import { QuickFilterPopoverMenuGroup } from './quick-filter-popover-menu-group';

type ViewFilter = 'CURRENT' | 'EXPIRED' | null;
export type RecurrenceFilter = 'ALL' | 'RECURRING_ONLY' | 'SINGLE_ONLY' | null;

const DEFAULT_VIEW_FILTER: ViewFilter = 'CURRENT';
const DEFAULT_RECURRENCE_FILTER: RecurrenceFilter = 'ALL';

const viewOptions: { label: string; value: ViewFilter }[] = [
  { label: i18next.t('Current', { ns: 'schedule-settings' }), value: 'CURRENT' },
  { label: i18next.t('Expired', { ns: 'schedule-settings' }), value: 'EXPIRED' },
];

const recurrenceOptions: { label: string; value: RecurrenceFilter }[] = [
  { label: i18next.t('All Occurrences', { ns: 'schedule-settings' }), value: 'ALL' },
  { label: i18next.t('Recurring Only', { ns: 'schedule-settings' }), value: 'RECURRING_ONLY' },
  { label: i18next.t('Single Occurrences', { ns: 'schedule-settings' }), value: 'SINGLE_ONLY' },
];

export interface QuickFiltersState {
  view: ViewFilter;
  recurrence: RecurrenceFilter;
}

type QuickFiltersProps = {
  filters: QuickFiltersState;
  onFiltersChange: (filters: QuickFiltersState) => void;
};

export const QuickFilters = ({ filters, onFiltersChange }: QuickFiltersProps) => {
  const { t } = useTranslation('schedule-settings');
  const popoverMenuProps = usePopoverMenu({ placement: 'bottom-start' });

  const { formProps, getFieldProps, reset, changedValues } = useForm({
    fields: {
      view: { type: 'radio', value: filters.view || 'CURRENT' },
      recurrence: { type: 'radio', value: filters.recurrence || 'ALL' },
    },
    onSubmit: (values) => {
      onFiltersChange(values as QuickFiltersState);
      popoverMenuProps.close();
    },
    computeChangedValues: true,
  });

  useEffect(() => {
    if (filters.view || filters.recurrence) {
      reset();
    }
    // Not adding `reset` to the dependency array as it is a new reference on every render, causing infinite loops.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.view, filters.recurrence]);

  const filterLabel = useMemo(() => {
    const viewLabel = viewOptions.find((option) => option.value === (filters.view || DEFAULT_VIEW_FILTER))?.label;
    const recurrenceLabel = recurrenceOptions.find(
      (option) => option.value === (filters.recurrence || DEFAULT_RECURRENCE_FILTER)
    )?.label;
    return `${viewLabel}, ${recurrenceLabel}`;
  }, [filters.view, filters.recurrence]);

  return (
    <>
      <Chip.DropdownFilter {...popoverMenuProps.getTriggerProps()} css={{ maxWidth: 200 }}>
        <Text css={styles.truncate}>{filterLabel}</Text>
      </Chip.DropdownFilter>

      <PopoverMenu {...popoverMenuProps.getMenuProps()} initialFocus={0}>
        <form {...formProps}>
          <QuickFilterPopoverMenuGroup title={t('View')}>
            <RadioField {...getFieldProps('view')} labelPlacement='right'>
              {viewOptions.map(({ label, value }) => (
                <RadioField.Radio key={value} value={value || ''}>
                  {label}
                </RadioField.Radio>
              ))}
            </RadioField>
          </QuickFilterPopoverMenuGroup>
          <QuickFilterPopoverMenuGroup title={t('Recurrence')}>
            <RadioField {...getFieldProps('recurrence')} labelPlacement='right'>
              {recurrenceOptions.map(({ label, value }) => (
                <RadioField.Radio key={value} value={value || ''}>
                  {label}
                </RadioField.Radio>
              ))}
            </RadioField>
          </QuickFilterPopoverMenuGroup>
          <div css={{ padding: 'var(--fabric-space-gap-xs) var(--fabric-space-gap-md)' }}>
            <Button type='submit' value='primary' css={{ width: '100%' }} disabled={!changedValues}>
              {t('Apply')}
            </Button>
          </div>
        </form>
      </PopoverMenu>
    </>
  );
};
